import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import usePagedList from '../../hooks/usePagedList';
import {
  PROJECT_COLUMNS,
  SEARCH_FIELDS,
  getFilterState,
  isDueSoon,
  isTestProjectFlag,
  projectStatusOf,
} from './projectColumns';

// Earlier versions cached pages of project records here; never read again
const LEGACY_CACHE_KEY = 'projectsPageCache';

const formatTimestamp = (timestamp) => {
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
  }
  return timestamp || '';
};

/**
 * Liste için her alan metne çevrilir (eski davranış; modallar da bu biçimi
 * bekliyor). Ham veri gereken yerler dokümanı Firestore'dan yeniden okur.
 */
export const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value.seconds) return formatTimestamp(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const formatProjectDoc = (docSnap, data) => ({
  id: docSnap.id,
  ...Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = formatValue(value);
    return acc;
  }, {}),
});

// Aktif/Pasif bilgisi eskiden projectPhase'te tutuluyordu; okurken
// projectStatus'a taşınır (ve dokümana yazılır), durum yoksa "Aktif" olur.
const migrateStatus = (docSnap, data) => {
  const migrated = { ...data };
  const phaseLower = data.projectPhase?.toLowerCase();
  const write = (fields) => updateDoc(docSnap.ref, fields).catch((error) => console.error('Migration error:', error));

  if (phaseLower === 'aktif' || phaseLower === 'devam ediyor') {
    migrated.projectStatus = 'Aktif';
    migrated.projectPhase = '';
    write({ projectStatus: 'Aktif', projectPhase: '' });
  } else if (phaseLower === 'pasif' || phaseLower === 'tamamlandı') {
    migrated.projectStatus = 'Pasif';
    migrated.projectPhase = '';
    write({ projectStatus: 'Pasif', projectPhase: '' });
  } else if (!data.projectStatus) {
    migrated.projectStatus = 'Aktif';
    write({ projectStatus: 'Aktif' });
  }
  return migrated;
};

const hasActiveFilter = (filters, searchTerm, showExpiringOnly, showTestProjectsOnly) => {
  const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
  const hasActiveFilters = Object.keys(filters).some((key) => getFilterState(key, filters[key]) === 'applied');
  return Boolean(hasActiveSearch || hasActiveFilters || showExpiringOnly || showTestProjectsOnly);
};

const filterProjects = (projects, { filters, searchTerm, showExpiringOnly, showTestProjectsOnly }) => {
  let filtered = projects;

  // Arama (müşteri, kısa başlık, departman) - en az 3 karakter
  if (searchTerm && searchTerm.trim().length >= 3) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filtered = filtered.filter((project) => SEARCH_FIELDS.some((field) => (
      project[field] && project[field].toString().toLowerCase().includes(lowercasedSearch)
    )));
  }

  if (showExpiringOnly) filtered = filtered.filter(isDueSoon);
  if (showTestProjectsOnly) filtered = filtered.filter(isTestProjectFlag);

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (!value) return;
    const type = PROJECT_COLUMNS[key]?.type;
    if (type === 'select') {
      // Seçim filtreleri birebir eşleşir (durum boşsa "Aktif" sayılır)
      filtered = filtered.filter((project) => (key === 'projectStatus' ? projectStatusOf(project) : project[key]) === value);
    } else if (type === 'date') {
      filtered = filtered.filter((project) => project[key] && project[key].includes(value));
    } else {
      // Metin filtreleri en az 3 karakterde
      const filterValue = String(value);
      if (filterValue.trim().length >= 3) {
        filtered = filtered.filter((project) => String(project[key] || '').toLowerCase().includes(filterValue.toLowerCase()));
      }
    }
  });

  return filtered;
};

/**
 * Proje Yönetimi listesi. Projects koleksiyonu tek seferde okunur (orderBy
 * bilerek yok: alanı olmayan dokümanları düşürüyordu); sayaçlar, arama,
 * filtreler ve sayfalama tarayıcıda aynı küme üzerinde çalışır. Test
 * projelerini yalnızca ana admin görür.
 */
const useProjectDirectory = (isMainAdmin) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [allProjects, setAllProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showTestProjectsOnly, setShowTestProjectsOnly] = useState(false);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Projects'));
      let projects = querySnapshot.docs.map((docSnap) => formatProjectDoc(docSnap, migrateStatus(docSnap, docSnap.data())));
      // Test projelerini yalnızca ana admin görür ve sayar
      if (!isMainAdmin) projects = projects.filter((project) => !isTestProjectFlag(project));
      projects.sort((a, b) => String(a.customer || '').localeCompare(String(b.customer || ''), 'tr', { sensitivity: 'base' }));
      setAllProjects(projects);
      setHasLoaded(true);
      return projects;
    } catch (error) {
      console.error('Error fetching all projects:', error);
      toast.error(tRef.current('projectManagement.toast.loadError'));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [isMainAdmin]);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (e) {
      // Storage unavailable; nothing to clean up
    }
    fetchProjects();
  }, [fetchProjects]);

  // Boş yer tutucular (isEmpty) listede gösterilmez; sayımlarda kalır
  const listedProjects = useMemo(() => allProjects.filter((project) => !project.isEmpty), [allProjects]);
  const filteredProjects = useMemo(
    () => filterProjects(listedProjects, { filters, searchTerm, showExpiringOnly, showTestProjectsOnly }),
    [listedProjects, filters, searchTerm, showExpiringOnly, showTestProjectsOnly]
  );

  const stats = useMemo(() => ({
    total: allProjects.length,
    active: allProjects.filter((project) => projectStatusOf(project) === 'Aktif').length,
    inactive: allProjects.filter((project) => project.projectStatus === 'Pasif').length,
    dueSoon: listedProjects.filter(isDueSoon).length,
  }), [allProjects, listedProjects]);

  const isFilterActive = hasActiveFilter(filters, searchTerm, showExpiringOnly, showTestProjectsOnly);
  const paging = usePagedList(filteredProjects, {
    resetKey: `${JSON.stringify(filters)}|${searchTerm}|${showExpiringOnly}|${showTestProjectsOnly}`,
  });

  const handleFilter = useCallback((column, value) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  /** Applies a change to one project in the list without reloading. */
  const patchProject = useCallback((projectId, changes) => {
    setAllProjects((prev) => prev.map((project) => (project.id === projectId ? { ...project, ...changes } : project)));
  }, []);

  return {
    allProjects,
    filteredProjects,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    showExpiringOnly,
    setShowExpiringOnly,
    showTestProjectsOnly,
    setShowTestProjectsOnly,
    isFilterActive,
    isLoading,
    hasLoaded,
    stats,
    patchProject,
    ...paging,
    refreshProjects: fetchProjects,
  };
};

export default useProjectDirectory;
